/**
 * LotSmith — Onboarding wizard.
 *
 * Goal: get a brand-new visitor to a populated dashboard in under 60 s.
 * Path: 4 steps, every step skippable, magic-link is async (background)
 * so the user reaches the dashboard demo even before clicking the email.
 *
 *   Step 1  Welcome — email + magic-link
 *   Step 2  Prop firm preset
 *   Step 3  Account info
 *   Step 4  EA install (or skip → demo mode)
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type Step = 1 | 2 | 3 | 4;

export interface OnboardingState {
  email: string;
  goal: string;
  propFirm: string;
  phase: string;
  brokerLogin: string;
  startBalance: number;
  currency: string;
}

const GOALS = [
  { id: "pass_first",     label: "Pass my first challenge", desc: "Keep risk small, stop after bad sequences." },
  { id: "stay_funded",    label: "Stay funded", desc: "Protect daily loss and avoid rule mistakes." },
  { id: "multi_account",  label: "Manage multiple accounts", desc: "Track separate prop accounts in one dashboard." },
] as const;

const PROP_FIRMS = [
  { id: "FTMO",            label: "FTMO" },
  { id: "FUNDED_NEXT",     label: "FundedNext" },
  { id: "MFF",             label: "MyForexFunds" },
  { id: "FIVE_PERCENTERS", label: "The 5%ers" },
  { id: "TOPSTEP",         label: "TopStep" },
  { id: "E8",              label: "E8" },
  { id: "FUNDING_PIPS",    label: "Funding Pips" },
  { id: "PERSONAL",        label: "Personal account" },
  { id: "OTHER",           label: "Other" },
] as const;

const PHASES = [
  { id: "EVAL_1", label: "Phase 1 (challenge)" },
  { id: "EVAL_2", label: "Phase 2 (verification)" },
  { id: "FUNDED", label: "Funded" },
  { id: "DEMO",   label: "Demo / practice" },
] as const;

// Mirror of the FUNDED-phase rule data in worker-license/src/prop-firm-rules.ts.
// Kept compact + client-side so Step 2 can show a preview without an API
// roundtrip. Drift risk is real but small — this only renders during the
// 30-second decision moment, the server is the source of truth thereafter.
interface FirmPreview {
  daily: string;
  total: string;
  target: string;
  resetTz: string;
  news: string;
}
const FIRM_PREVIEWS: Record<string, FirmPreview> = {
  FTMO:            { daily: "5 %",  total: "10 %",  target: "10 %", resetTz: "Europe/Prague",     news: "Funded only: ±2 min blackout, red folder" },
  FUNDED_NEXT:     { daily: "5 %",  total: "10 %",  target: "8 %",  resetTz: "Europe/London",     news: "Discouraged — 40 % profit haircut on news trades" },
  MFF:             { daily: "5 %",  total: "10 %",  target: "8 %",  resetTz: "America/New_York",  news: "USD high-impact: ±5 min blackout" },
  FIVE_PERCENTERS: { daily: "4 %",  total: "8 %",   target: "6 %",  resetTz: "Europe/London",     news: "±3 min blackout, red folder" },
  TOPSTEP:         { daily: "3 %",  total: "6 %",   target: "6 %",  resetTz: "America/Chicago",   news: "SimFi: ±5 min blackout" },
  E8:              { daily: "5 %",  total: "8 %",   target: "8 %",  resetTz: "Europe/Prague",     news: "News bracketing + bots banned" },
  FUNDING_PIPS:    { daily: "5 %",  total: "10 %",  target: "8 %",  resetTz: "Europe/London",     news: "No double-sided orders during news" },
  PERSONAL:        { daily: "5 %",  total: "10 %",  target: "10 %", resetTz: "UTC",               news: "Allowed (no firm rules)" },
  OTHER:           { daily: "5 %",  total: "10 %",  target: "10 %", resetTz: "Europe/Prague",     news: "Verify with your firm's TOS" },
};

export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [state, setState] = useState<OnboardingState>({
    email: "",
    goal: "",
    propFirm: "",
    phase: "EVAL_1",
    brokerLogin: "",
    startBalance: 100_000,
    currency: "USD",
  });
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof OnboardingState>(k: K, v: OnboardingState[K]) {
    setState((s) => ({ ...s, [k]: v }));
  }

  function finish(mode: "email-sent" | "demo") {
    try {
      localStorage.setItem("lsm_onboarded_at", new Date().toISOString());
      localStorage.setItem("lsm_demo_mode", mode === "demo" ? "1" : "0");
      if (state.email) localStorage.setItem("lsm_pending_email", state.email);
      if (state.propFirm) localStorage.setItem("lsm_prop_firm", state.propFirm);
      if (state.goal) localStorage.setItem("lsm_trading_goal", state.goal);
    } catch {
      // localStorage may be unavailable in private mode — ignore.
    }
    // Best-effort account creation. The EA will auto-provision the same
    // (broker_login, broker_server) later if this fails.
    if (mode === "email-sent" && state.brokerLogin && state.startBalance > 0) {
      const license = licenseApiBase();
      if (license) {
        void fetch(`${license}/v1/accounts`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            propFirm: state.propFirm || "PERSONAL",
            phase: state.phase,
            brokerLogin: state.brokerLogin,
            brokerName: "Onboarding wizard",
            brokerServer: state.brokerLogin,   // placeholder; EA refines on first push
            currency: state.currency,
            startBalance: state.startBalance,
          }),
        }).catch(() => {
          // Worker may not be reachable in dev — that's fine, the EA will
          // pick this up on first auto-provision when it attaches.
        });
      }
    }
    navigate("/app", { replace: true });
  }

  async function submitEmail() {
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(state.email)) {
      setError("Enter a valid email.");
      return;
    }
    const auth = apiBase();
    if (auth) {
      // Fire-and-forget: don't gate the wizard on the network call.
      void fetch(`${auth}/v1/auth/magic-link`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: state.email }),
      }).catch(() => {
        // Worker may not be reachable in dev — that's fine, we already
        // continue the wizard and the link can be requested again.
      });
    }
    setStep(2);
  }

  return (
    <div className="onb">
      <Link to="/" className="onb__brand">LotSmith</Link>

      <div className="onb__card">
        <div className="onb__main">
          <Stepper step={step} />

          {step === 1 && (
            <section>
              <div className="onb__kicker">Start free</div>
              <h1>Set up your trade panel</h1>
              <p className="onb__lead">
                Free works offline. Add an email if you want cloud sync,
                alerts, and dashboard history later.
              </p>
              <label className="label" htmlFor="onb-email">Email</label>
              <input
                id="onb-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                className="input"
                value={state.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
              />

              <label className="label" style={{ marginTop: 16 }}>
                Trading goal <span className="onb__optional">(optional)</span>
              </label>
              <div className="onb__goals">
                {GOALS.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className={`onb-pick onb-pick--stack${state.goal === g.id ? " is-active" : ""}`}
                    onClick={() => update("goal", g.id)}
                  >
                    <span>{g.label}</span>
                    <small>{g.desc}</small>
                  </button>
                ))}
              </div>

              {error && <div className="form-error">{error}</div>}
              <div className="onb__actions">
                <button type="button" className="btn btn--ghost" onClick={() => finish("demo")}>
                  Try demo
                </button>
                <button type="button" className="btn btn--primary" onClick={submitEmail}>
                  Continue
                </button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section>
              <div className="onb__kicker">Rules preset</div>
              <h1>Which prop firm?</h1>
              <p className="onb__lead">We&rsquo;ll preload the starting limits. You can change them later.</p>
              <div className="onb__grid">
                {PROP_FIRMS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`onb-pick${state.propFirm === f.id ? " is-active" : ""}`}
                    onClick={() => update("propFirm", f.id)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {state.propFirm && (() => {
                const preview = FIRM_PREVIEWS[state.propFirm];
                if (!preview) return null;
                return (
                  <div className="onb-firm-preview">
                    <div className="onb-firm-preview__hdr">Default rules — funded phase</div>
                    <dl className="onb-firm-preview__list">
                      <div><dt>Daily loss</dt><dd>{preview.daily}</dd></div>
                      <div><dt>Max drawdown</dt><dd>{preview.total}</dd></div>
                      <div><dt>Profit target (P1)</dt><dd>{preview.target}</dd></div>
                      <div><dt>Reset timezone</dt><dd>{preview.resetTz}</dd></div>
                      <div><dt>News policy</dt><dd>{preview.news}</dd></div>
                    </dl>
                    <p className="onb-firm-preview__fine">
                      Server rules are authoritative after cloud sync. Local EA guard keeps working offline.
                    </p>
                  </div>
                );
              })()}

              <div className="onb__actions">
                <button type="button" className="btn btn--ghost" onClick={() => setStep(3)}>
                  Skip
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setStep(3)}
                  disabled={!state.propFirm}
                >
                  Continue
                </button>
              </div>
            </section>
          )}

          {step === 3 && (
            <section>
              <div className="onb__kicker">Account</div>
              <h1>Account details</h1>
              <p className="onb__lead">
                This lets the dashboard create the right account row before the EA first syncs.
              </p>
              <label className="label" htmlFor="onb-broker">Broker login <span className="onb__optional">(optional)</span></label>
              <input
                id="onb-broker"
                className="input"
                value={state.brokerLogin}
                onChange={(e) => update("brokerLogin", e.target.value)}
                placeholder="1234567"
                inputMode="numeric"
              />
              <label className="label" htmlFor="onb-phase">Phase</label>
              <select
                id="onb-phase"
                className="input"
                value={state.phase}
                onChange={(e) => update("phase", e.target.value)}
              >
                {PHASES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <div className="onb__row">
                <div>
                  <label className="label" htmlFor="onb-bal">Start balance</label>
                  <input
                    id="onb-bal"
                    className="input num"
                    type="number"
                    inputMode="numeric"
                    value={state.startBalance}
                    onChange={(e) => update("startBalance", Number(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="onb-ccy">Currency</label>
                  <select
                    id="onb-ccy"
                    className="input"
                    value={state.currency}
                    onChange={(e) => update("currency", e.target.value)}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                  </select>
                </div>
              </div>
              <div className="onb__actions">
                <button type="button" className="btn btn--ghost" onClick={() => setStep(4)}>
                  Skip
                </button>
                <button type="button" className="btn btn--primary" onClick={() => setStep(4)}>
                  Continue
                </button>
              </div>
            </section>
          )}

          {step === 4 && (
            <Step4Install state={state} onFinish={finish} />
          )}
        </div>

        <OnboardingSummary state={state} step={step} />
      </div>
    </div>
  );
}

// ── Step 4: full EA install guide ─────────────────────────────────
interface Step4Props {
  state: OnboardingState;
  onFinish: (mode: "email-sent" | "demo") => void;
}

function Step4Install({ state, onFinish }: Step4Props) {
  const license = licenseApiBase();
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function getToken() {
    if (!license) {
      setErr("License API not configured. Try the demo instead.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${license}/v1/devices`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          label: state.brokerLogin ? `MT5 — broker #${state.brokerLogin}` : "MT5 — first device",
          eaVersion: "1.69",
        }),
      });
      if (r.status === 401) {
        setErr("Sign in first — check the magic-link email we sent.");
        return;
      }
      if (!r.ok) throw new Error(`devices ${r.status}`);
      const j = await r.json() as { token: string };
      setToken(j.token);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't generate a device token.");
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Some browsers block clipboard from non-https / non-user gesture.
      // Fall back to selecting the visible token field.
    }
  }

  return (
    <section>
      <h1>Install the EA</h1>
      <p className="onb__lead">
        Five-minute install. Generate your device token, paste it into MT5, and the
        dashboard fills with your real trades.
      </p>

      <ol className="onb__install">
        <li>
          <strong>Download the EA</strong>
          <p>
            <a href="/downloads/LotSmith.ex5" className="lnk" download>Download LotSmith.ex5</a>
            {" "}— version 1.69, single file, no installer.
          </p>
        </li>

        <li>
          <strong>Drop into MT5's MQL5 folder</strong>
          <p>
            Open MT5 → <em>File → Open Data Folder</em> → <code>MQL5 → Experts</code>.
            Drop <code>LotSmith.ex5</code> in, restart MT5 (or right-click Experts in the
            Navigator → Refresh).
          </p>
        </li>

        <li>
          <strong>Generate your device token</strong>
          {!token && (
            <div className="onb__token-row">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void getToken()}
                disabled={busy}
              >
                {busy ? "Generating…" : "Generate token"}
              </button>
              {err && <div className="form-error">{err}</div>}
            </div>
          )}
          {token && (
            <div className="onb__token">
              <code className="onb__token-val">{token}</code>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void copy()}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
            </div>
          )}
          {token && (
            <p className="onb__token-hint">
              You'll see this once. If you lose it, generate a new one in
              <em> Settings → Devices</em>.
            </p>
          )}
        </li>

        <li>
          <strong>Drag the EA onto any chart</strong>
          <p>
            Drag <code>LotSmith</code> from <em>Navigator → Expert Advisors</em> onto a chart.
            The inputs dialog opens. Paste the token into <code>LSM_InpDeviceToken</code>.
            Click OK. The panel appears top-left.
          </p>
        </li>

        <li>
          <strong>Whitelist the WebRequest URL</strong>
          <p>
            MT5 → <em>Tools → Options → Expert Advisors</em> → check
            "Allow WebRequest for listed URL", then add:
          </p>
          <code className="onb__code">https://lotsmith-license.workers.dev</code>
          <p className="onb__token-hint">
            Without this, the EA stays in offline / FREE mode (no cloud sync, no
            Telegram alerts).
          </p>
        </li>
      </ol>

      <div className="onb__actions">
        <button type="button" className="btn btn--ghost" onClick={() => onFinish("demo")}>
          Explore demo first
        </button>
        <button type="button" className="btn btn--primary" onClick={() => onFinish("email-sent")}>
          I&rsquo;ve installed it
        </button>
      </div>
    </section>
  );
}

function OnboardingSummary({ state, step }: { state: OnboardingState; step: Step }) {
  const firm = PROP_FIRMS.find((f) => f.id === state.propFirm)?.label ?? "Personal account";
  const phase = PHASES.find((p) => p.id === state.phase)?.label ?? "Phase 1";
  const goal = GOALS.find((g) => g.id === state.goal)?.label ?? "Trade with guardrails";
  return (
    <aside className="onb-summary">
      <div className="onb-summary__eyebrow">Setup preview</div>
      <div className="onb-summary__title">LotSmith v1.69</div>
      <dl className="onb-summary__list">
        <div><dt>Mode</dt><dd>{step === 4 ? "Install EA" : "Free onboarding"}</dd></div>
        <div><dt>Goal</dt><dd>{goal}</dd></div>
        <div><dt>Firm</dt><dd>{firm}</dd></div>
        <div><dt>Phase</dt><dd>{phase}</dd></div>
        <div><dt>Balance</dt><dd>{state.currency} {state.startBalance.toLocaleString("en-US")}</dd></div>
      </dl>
      <div className="onb-summary__note">
        Free mode keeps the EA usable offline. Cloud sync activates only after login and device token setup.
      </div>
    </aside>
  );
}

function Stepper({ step }: { step: Step }) {
  return (
    <div className="onb__stepper">
      {[1, 2, 3, 4].map((s) => (
        <div key={s} className={`onb__step${s <= step ? " is-done" : ""}${s === step ? " is-current" : ""}`}>
          <span className="onb__step-dot" />
          <span className="onb__step-label">
            {s === 1 ? "Welcome" : s === 2 ? "Prop firm" : s === 3 ? "Account" : "Install"}
          </span>
        </div>
      ))}
    </div>
  );
}

function apiBase(): string {
  return (import.meta.env.VITE_AUTH_API_BASE as string | undefined) ?? "";
}

function licenseApiBase(): string {
  return (import.meta.env.VITE_LICENSE_API_BASE as string | undefined) ?? "";
}
